/**
 * Landing (`/`, §7.1): your matches on this device (each fetched lazily, cached for offline),
 * create / join, a three-card "How it works" for first-timers, and a demo tour in demo mode.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MatchSummary } from '@seven-dawns/shared';
import { useBackend } from '../../app/backendContext';
import { Link } from '../../app/Link';
import { navigate } from '../../app/router';
import { DEMO_SHORTCUTS } from '../../dev/demoShortcuts';
import { formatDuration } from '../../lib/time';
import { ApiError } from '../../net/api';
import type { MatchSession, PendingJoin } from '../../net/session';
import { useNowMs } from '../../state/clock';
import { Button } from '../../ui/Button';
import { buttonClass } from '../../ui/buttonClass';
import { cx } from '../../ui/cx';
import { focusHeading } from '../../ui/focus';
import { Icon, type IconName } from '../../ui/Icon';
import { ListRow } from '../../ui/ListRow';
import { PixelIcon } from '../../ui/PixelIcon';
import type { PixelIconName } from '../../game/assets/pixelIcons';
import { PixelSpinner } from '../../ui/PixelSpinner';
import { Backdrop } from './Backdrop';
import { summaryLine } from './summaryLine';
import styles from './LandingScreen.module.css';

type Entry = { kind: 'session'; session: MatchSession } | { kind: 'pending'; pending: PendingJoin };

export function LandingScreen() {
  const backend = useBackend();
  const [entries, setEntries] = useState<Entry[]>(() => [
    ...backend.sessions.allPending().map((pending): Entry => ({ kind: 'pending', pending })),
    ...backend.sessions.all().map((session): Entry => ({ kind: 'session', session })),
  ]);
  const h1 = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    if (document.body.dataset.wbNavigated === 'true') focusHeading(h1.current);
    document.body.dataset.wbNavigated = 'true';
  }, []);

  const remove = (code: string) => {
    backend.sessions.remove(code);
    backend.sessions.clearPending(code);
    setEntries((list) =>
      list.filter((e) => (e.kind === 'session' ? e.session.code : e.pending.code) !== code),
    );
  };

  const empty = entries.length === 0;
  const [showHow, setShowHow] = useState(false);

  return (
    <div className={styles.landing}>
      <header className={styles.hero}>
        <Backdrop />
        <div className={styles.heroText}>
          <h1 ref={h1} tabIndex={-1} className={styles.title}>
            Seven Dawns
          </h1>
          <p className={styles.tagline}>A strategy game played over a week, a few minutes a day.</p>
        </div>
      </header>

      <main id="main" className={styles.main}>
        <div className={styles.actions}>
          <Button variant="primary" size="l" block icon="plus" onClick={() => navigate('/new')}>
            Create a match
          </Button>
          <Button variant="secondary" size="l" block icon="userPlus" onClick={() => navigate('/join')}>
            Join with a code
          </Button>
        </div>

        {!empty && (
          <section aria-labelledby="your-matches" className={styles.section}>
            <h2 id="your-matches" className={styles.sectionTitle}>
              Your matches
            </h2>
            <ul className={styles.list}>
              {entries.map((e) =>
                e.kind === 'pending' ? (
                  <PendingRow key={`p-${e.pending.code}`} pending={e.pending} />
                ) : (
                  <MatchRow
                    key={e.session.code}
                    session={e.session}
                    onRemove={() => remove(e.session.code)}
                  />
                ),
              )}
            </ul>
          </section>
        )}

        {(empty || showHow) && <HowItWorks />}

        {backend.mode === 'demo' && <DemoTour />}

        <footer className={styles.footer}>
          <Link to="/settings" className={styles.footerLink}>
            <Icon name="settings" size={18} /> Settings
          </Link>
          {!empty && (
            <button
              type="button"
              className={styles.footerLink}
              aria-expanded={showHow}
              onClick={() => setShowHow(!showHow)}
            >
              <Icon name="help" size={18} /> How it works
            </button>
          )}
        </footer>
      </main>
    </div>
  );
}

const STEPS: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'route',
    title: 'Plan',
    text: 'Send armies across the realm and leave them up to 3 queued orders for while you are away.',
  },
  {
    icon: 'moon',
    title: 'Leave',
    text: 'Battles take hours, not seconds. Your armies follow their doctrine until you return.',
  },
  {
    icon: 'dawn',
    title: 'Return to the Dawn Dispatch',
    text: 'Every morning, one summary of what happened overnight and what needs you.',
  },
];

function HowItWorks() {
  return (
    <section aria-labelledby="how-it-works" className={styles.section}>
      <h2 id="how-it-works" className={styles.sectionTitle}>
        How it works
      </h2>
      <ol className={styles.steps}>
        {STEPS.map((s, i) => (
          <li key={s.title} className={styles.step}>
            <span className={styles.stepIcon} aria-hidden="true">
              <Icon name={s.icon} size={24} />
            </span>
            <span>
              <span className={styles.stepTitle}>
                <span className={styles.stepNum}>{i + 1}</span> {s.title}
              </span>
              <span className={styles.stepText}>{s.text}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

type RowState =
  | { state: 'loading'; cached: CachedSummary | null }
  | { state: 'ready'; summary: MatchSummary }
  | { state: 'missing' }
  | { state: 'error'; cached: CachedSummary | null };

interface CachedSummary {
  summary: MatchSummary;
  atMs: number;
}

function MatchRow({ session, onRemove }: { session: MatchSession; onRemove(): void }) {
  const backend = useBackend();
  const [row, setRow] = useState<RowState>(() => ({
    state: 'loading',
    cached: backend.memory.get<CachedSummary | null>(session.code, 'summary', null),
  }));
  const nowMs = useNowMs();

  const [reload, setReload] = useState(0);
  const retry = () => {
    setRow({
      state: 'loading',
      cached: backend.memory.get<CachedSummary | null>(session.code, 'summary', null),
    });
    setReload((n) => n + 1);
  };

  useEffect(() => {
    const abort = new AbortController();
    backend.api.getMatch(session.code, abort.signal).then(
      (summary) => {
        backend.memory.set(session.code, 'summary', { summary, atMs: Date.now() } satisfies CachedSummary);
        setRow({ state: 'ready', summary });
      },
      (error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (error instanceof ApiError && error.status === 404) setRow({ state: 'missing' });
        else
          setRow({
            state: 'error',
            cached: backend.memory.get<CachedSummary | null>(session.code, 'summary', null),
          });
      },
    );
    return () => abort.abort();
  }, [backend, session.code, reload]);

  const name = session.matchName;
  const to = `/m/${session.code}`;
  const known = row.state === 'ready' ? row.summary : row.state === 'error' ? row.cached?.summary : undefined;
  const leading = <MatchGlyph summary={known} />;

  if (row.state === 'missing') {
    return (
      <ListRow
        tone="muted"
        leading={leading}
        title={name}
        subtitle="Match no longer exists"
        chevron={false}
        action={
          <Button size="s" variant="danger" onClick={onRemove}>
            Remove
          </Button>
        }
      />
    );
  }

  if (row.state === 'error') {
    const cached = row.cached;
    return (
      <ListRow
        tone={cached ? 'default' : 'danger'}
        leading={leading}
        title={name}
        to={cached ? to : undefined}
        subtitle={cached ? summaryLine(cached.summary, session.playerName) : "Couldn't reach this match"}
        meta={cached ? `Last updated ${formatDuration((nowMs - cached.atMs) / 60_000)} ago` : undefined}
        chevron={!!cached}
        action={
          <Button size="s" icon="refresh" onClick={retry}>
            Retry
          </Button>
        }
      />
    );
  }

  const summary = known;
  const status = summary?.status;
  return (
    <ListRow
      to={to}
      leading={leading}
      title={name}
      subtitle={
        summary ? (
          summaryLine(summary, session.playerName)
        ) : (
          <span className={styles.loadingLine}>
            <PixelSpinner /> Checking…
          </span>
        )
      }
      meta={`As ${session.playerName} · code ${session.code}`}
      trailing={
        status ? (
          <span className={cx(styles.badge, styles[status])}>
            {status === 'running' ? 'Live' : status === 'lobby' ? 'Lobby' : 'Ended'}
          </span>
        ) : undefined
      }
    />
  );
}

function PendingRow({ pending }: { pending: PendingJoin }) {
  return (
    <ListRow
      to={`/m/${pending.code}/pending`}
      tone="accent"
      leading={
        <span className={cx(styles.rowIcon, styles.rowIconPending)}>
          <Icon name="hourglass" size={18} />
        </span>
      }
      title={pending.matchName}
      subtitle="Waiting for a teammate to let you in"
      meta={`As ${pending.playerName} · code ${pending.code}`}
    />
  );
}

/**
 * Leading art for a match row: a pixel glyph for the match state (lobby flag, live swords, ended
 * trophy) on a state-coloured tile — never a repeated initial letter.
 */
function MatchGlyph({ summary }: { summary: MatchSummary | undefined }) {
  const status = summary?.status;
  const glyph: PixelIconName =
    status === 'running' ? 'swords' : status === 'finished' ? 'trophy' : status === 'lobby' ? 'flag' : 'map';
  return (
    <span className={cx(styles.rowIcon, status && styles[`rowIcon-${status}`])}>
      <PixelIcon name={glyph} size={24} />
    </span>
  );
}

function DemoTour() {
  const groups = [...new Set(DEMO_SHORTCUTS.map((s) => s.group))];
  return (
    <section aria-labelledby="demo-tour" className={cx(styles.section, styles.tour)}>
      <div className={styles.tourHead}>
        <h2 id="demo-tour" className={styles.sectionTitle}>
          Demo tour
        </h2>
        <p className={styles.tourText}>
          This is an offline demo with a sample match on day 3. Jump straight to any screen:
        </p>
      </div>
      {groups.map((g) => (
        <div key={g} className={styles.tourGroup}>
          <h3 className={styles.tourGroupTitle}>{g}</h3>
          <ul className={styles.tourLinks}>
            {DEMO_SHORTCUTS.filter((s) => s.group === g).map((s) => (
              <li key={s.path}>
                <Link to={s.path} className={buttonClass('secondary', 's')}>
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
